// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { getRandomIntegerInclusive } from "./random.js";

/**
 * Calculates the delay interval for retry attempts using exponential delay with jitter.
 * @param retryAttempt - The current retry attempt number.
 * @param config - The exponential retry configuration.
 * @returns An object containing the calculated retry delay.
 */
export function calculateRetryDelay(
  retryAttempt: number,
  config: {
    retryDelayInMs: number;
    maxRetryDelayInMs: number;
  },
): { retryAfterInMs: number } {
  // Exponentially increase the delay each time
  const exponentialDelay = config.retryDelayInMs * Math.pow(2, retryAttempt);

  // Don't let the delay exceed the maximum
  const clampedDelay = Math.min(config.maxRetryDelayInMs, exponentialDelay);

  // Allow the final value to have some "jitter" (within 50% of the delay size) so
  // that retries across multiple clients don't occur simultaneously.
  const retryAfterInMs = clampedDelay / 2 + getRandomIntegerInclusive(0, clampedDelay / 2);

  return { retryAfterInMs };
}
